import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { api, assetUrl } from "@/lib/api";
import { ytThumb, ytEmbed } from "@/lib/youtube";
import { MaskWords } from "@/components/site/primitives";
import { Reveal } from "@/components/site/Reveal";

const thumbFix = (e) => {
  if (e.currentTarget.src.includes("maxresdefault"))
    e.currentTarget.src = e.currentTarget.src.replace("maxresdefault", "mqdefault");
};

/* Minimalist editorial link — underlined eyebrow with a subtle arrow. */
function LibraryLink({ href, external, children, testid, className = "" }) {
  const cls =
    `group inline-flex items-center gap-2 eyebrow border-b border-ink/40 pb-1 hover:border-ink hover:gap-3 transition-all duration-300 ${className}`;
  const inner = (
    <>
      {children}
      <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </>
  );
  if (external)
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" data-cursor="link" data-testid={testid} className={cls}>
        {inner}
      </a>
    );
  return (
    <Link to={href} data-cursor="link" data-testid={testid} className={cls}>
      {inner}
    </Link>
  );
}

/* Uniform 16:9 card: thumbnail (object-contain, uncropped) → in-place player, discreet title below. */
function MediaCard({ item, title, subtitle, playing, onPlay, testid, cardButton }) {
  const cover = assetUrl(item.cover_image) || ytThumb(item.youtube_url);
  const canPlay = !!ytEmbed(item.youtube_url);
  return (
    <Reveal>
      {playing && canPlay ? (
        <div className="relative w-full overflow-hidden aspect-video bg-black" data-testid={`${testid}-player`}>
          <iframe
            key={item.id}
            src={ytEmbed(item.youtube_url, true)}
            title={title}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => canPlay && onPlay()}
          data-testid={testid}
          data-cursor="link"
          className="group relative block w-full overflow-hidden aspect-video bg-ink/5"
        >
          {cover && <img src={cover} alt={title} onError={thumbFix} className="w-full h-full object-contain" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/35 to-transparent" />
          {canPlay && (
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-14 h-14 rounded-full border border-white/60 text-white flex items-center justify-center backdrop-blur-sm transition-all duration-500 group-hover:bg-white group-hover:text-black">
                <Play size={18} className="ml-0.5" fill="currentColor" />
              </span>
            </span>
          )}
        </button>
      )}
      <div className="pt-4">
        {subtitle && <div className="eyebrow text-ink/45 mb-1.5">{subtitle}</div>}
        <h3 className="font-display text-lg md:text-xl leading-tight">{title}</h3>
        {cardButton && <div className="mt-3">{cardButton}</div>}
      </div>
    </Reveal>
  );
}

function Zone({ index, label, intro, items, g, playing, setPlaying, testid, renderCardButton, sectionButton }) {
  if (items.length === 0) return null;
  return (
    <section className="px-6 md:px-12 lg:px-20 pb-28 md:pb-40 border-t border-ink/12 pt-16 md:pt-24 first:border-0 first:pt-0" data-testid={`zone-${testid}`}>
      <div className="mb-12 md:mb-16">
        <div className="flex items-baseline gap-5">
          <span className="eyebrow text-ink/30 tnum">{index}</span>
          <MaskWords Tag="h2" text={label} className="display uppercase text-[10vw] md:text-[6vw] leading-[0.85] break-words" />
        </div>
        {intro && <p className="mt-5 text-base md:text-lg leading-[1.55] text-ink/60 max-w-2xl md:pl-16">{intro}</p>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 md:gap-x-12 gap-y-12 md:gap-y-16">
        {items.map((m) => (
          <MediaCard
            key={m.id}
            item={m}
            title={g(m, "title")}
            subtitle={g(m, "outlet")}
            testid={`${testid}-${m.id}`}
            playing={playing === m.id}
            onPlay={() => setPlaying(m.id)}
            cardButton={renderCardButton ? renderCardButton(m) : null}
          />
        ))}
      </div>
      {sectionButton && <div className="mt-14 md:mt-20 md:pl-16">{sectionButton}</div>}
    </section>
  );
}

export default function Library() {
  const { lang, t } = useLang();
  const { config } = useSiteConfig();
  const L = t.library;
  const [media, setMedia] = useState([]);
  const [playing, setPlaying] = useState(null); // single active player across whole page

  useEffect(() => {
    api.get("/courses").then((r) => setMedia(r.data)).catch(() => setMedia([]));
  }, []);

  const g = (c, k) => c[`${k}_${lang}`] || c[`${k}_ro`] || "";
  // Zones are driven ENTIRELY by the admin-chosen `zone` on each Media item. No auto placement.
  const inZone = (z) => media.filter((m) => m.zone === z);

  // Button URLs are language-neutral: read current lang, fall back to the other language.
  const libRO = config?.content?.ro?.library || {};
  const libEN = config?.content?.en?.library || {};
  const urlOf = (key) => (L?.[key] || libRO[key] || libEN[key] || "").trim();

  // Masterclasses: each card links to its own course page (/library/:slug) by default.
  // If an explicit URL is set in Admin, all cards use that external link instead.
  const masterUrl = urlOf("masterclassesBtnUrl");
  const masterLabel = L?.masterclassesBtn;
  const renderMasterButton = (m) => {
    if (!masterLabel) return null;
    if (masterUrl)
      return (
        <LibraryLink href={masterUrl} external testid={`masterclasses-btn-${m.id}`}>
          {masterLabel}
        </LibraryLink>
      );
    const slug = m[`slug_${lang}`] || m.slug;
    if (!slug) return null;
    return (
      <LibraryLink href={`/library/${slug}`} testid={`masterclasses-btn-${m.id}`}>
        {masterLabel}
      </LibraryLink>
    );
  };

  const sectionButton = (label, url, testid) =>
    label && url ? (
      <LibraryLink href={url} external testid={testid}>
        {label}
      </LibraryLink>
    ) : null;

  return (
    <div className="bg-paper text-ink">
      <section className="px-6 md:px-12 lg:px-20 pt-36 md:pt-48 pb-16 md:pb-24">
        <div className="eyebrow text-ink/50 mb-6">{L.label}</div>
        <MaskWords Tag="h1" text={L.title} className="display uppercase text-[22vw] md:text-[18vw] lg:text-[15vw] leading-[0.8] break-words" />
        <p className="mt-8 text-lg md:text-xl leading-[1.55] text-ink/70 max-w-2xl">{L.intro}</p>
      </section>

      <Zone
        index="01"
        label={L.perspectivesLabel}
        intro={L.perspectivesIntro}
        items={inZone("perspectives")}
        g={g}
        playing={playing}
        setPlaying={setPlaying}
        testid="perspectives"
        sectionButton={sectionButton(L?.perspectivesBtn, urlOf("perspectivesBtnUrl"), "perspectives-section-btn")}
      />
      <Zone
        index="02"
        label={L.masterclassesLabel}
        intro={L.masterclassesIntro}
        items={inZone("masterclasses")}
        g={g}
        playing={playing}
        setPlaying={setPlaying}
        testid="masterclasses"
        renderCardButton={renderMasterButton}
      />
      <Zone
        index="03"
        label={L.dialoguesLabel}
        intro={L.dialoguesIntro}
        items={inZone("dialogues")}
        g={g}
        playing={playing}
        setPlaying={setPlaying}
        testid="dialogues"
        sectionButton={sectionButton(L?.dialoguesBtn, urlOf("dialoguesBtnUrl"), "dialogues-section-btn")}
      />
    </div>
  );
}
